/**
 * The cost table arrives from the sync Lambda as a plain <table>. On a phone —
 * and on a laptop once the database has a Comment column — it is wider than the
 * page, and a horizontally scrolling table reads as "the data is cut off",
 * because macOS hides the scrollbar until you are already scrolling.
 *
 * So each table gets a scroll wrapper, and each row is restyled as a stacked
 * card on narrow screens (see style.css) — which needs every cell to know its
 * column name, copied here onto each body cell as data-label.
 *
 * On top of that sits the share calculator: tick the items you are actually in
 * on, say how many people each of those is shared between, and get your own
 * total. It runs entirely in the browser, so it needs neither a "Paid by"
 * column in Notion nor a roster of names — each person answers for themselves.
 */

/** Mirrors the server's formatter so both sides render money identically. */
function formatMoney(cents: number, symbol: string): string {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100).toLocaleString('en-US');
  return `${negative ? '−' : ''}${symbol}${whole}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * Money as rendered by formatMoney: an optional multi-character symbol ("CA$",
 * "CHF "), a grouped whole part, and two decimals.
 */
function parseMoney(raw: string | null | undefined): { cents: number; symbol: string } | null {
  const text = raw?.trim();
  if (!text) return null;
  const m = text.match(/^([^\d]*?)\s*([\d,]+)(?:\.(\d{1,2}))?$/);
  if (!m) return null;
  const whole = Number(m[2].replace(/,/g, ''));
  if (!Number.isFinite(whole)) return null;
  const frac = m[3] ? Number(m[3].padEnd(2, '0')) : 0;
  return { cents: whole * 100 + frac, symbol: m[1] || '$' };
}

const MAX_PARTY = 100;
const STATE_KEY = (slug: string): string => `bixl-cost-split-${slug}`;

/**
 * What this person picked, remembered per trip.
 *
 * `rows` holds one entry per ticked item; the value is that item's own head
 * count, or null to follow the trip-wide number. Keeping "follow" distinct from
 * a coincidentally equal number means changing the party size still moves every
 * row the reader never overrode.
 */
interface SplitState {
  party: number | null;
  rows: Record<string, number | null>;
}

function readState(slug: string): SplitState {
  try {
    const parsed = JSON.parse(localStorage.getItem(STATE_KEY(slug)) ?? '') as SplitState;
    const party = Number.isInteger(parsed?.party) ? parsed.party : null;
    const rows = parsed?.rows && typeof parsed.rows === 'object' ? parsed.rows : {};
    return { party, rows };
  } catch {
    return { party: null, rows: {} };
  }
}

function writeState(slug: string, state: SplitState): void {
  try {
    localStorage.setItem(STATE_KEY(slug), JSON.stringify(state));
  } catch {
    // Losing the picks between visits beats throwing inside a render.
  }
}

/** The table total, preferred from the exact attribute, else the Total row. */
function readTotal(table: HTMLTableElement): { cents: number; symbol: string } | null {
  const attr = table.dataset.totalCents;
  if (attr !== undefined && Number.isFinite(Number(attr))) {
    return { cents: Number(attr), symbol: table.dataset.currency || '$' };
  }
  // The data attributes are baked into the markdown at sync time, and that
  // markdown is stored. Requiring them would mean no existing trip showed a
  // calculator until someone re-synced it from Notion.
  return parseMoney(table.querySelector('tfoot .notion-costs-amount')?.textContent);
}

interface PickableRow {
  key: string;
  name: string;
  amountCents: number;
  checkbox: HTMLInputElement;
  countInput: HTMLInputElement;
  countWrap: HTMLElement;
  /**
   * Set once the reader edits this row's count. Tracked as a flag rather than
   * inferred by comparing numbers, because an override that happens to equal
   * the trip-wide size is still an override and must stop following it.
   */
  overridden: boolean;
}

function numberField(className: string, value: number): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'number';
  input.min = '1';
  input.max = String(MAX_PARTY);
  input.inputMode = 'numeric';
  input.className = className;
  input.value = String(value);
  return input;
}

function clampCount(input: HTMLInputElement): number {
  return Math.min(MAX_PARTY, Math.max(1, Math.floor(Number(input.value) || 1)));
}

/**
 * Turns each priced row's item cell into a tick box with its own head count.
 *
 * The control goes inside the existing first cell rather than in a new column:
 * the stacked-card layout maps every cell to its header by position, so adding
 * a column here would relabel every cell after it.
 */
function buildRowPickers(table: HTMLTableElement, onChange: () => void): PickableRow[] {
  const rows: PickableRow[] = [];

  table.querySelectorAll<HTMLTableRowElement>('tbody tr').forEach((row) => {
    const itemCell = row.cells[0];
    const amountCell = row.querySelector<HTMLElement>('.notion-costs-amount');
    if (!itemCell || !amountCell) return;

    // A row with no amount yet — an unbooked rental — has nothing to divide.
    const amount = parseMoney(amountCell.textContent);
    if (!amount) return;

    const name = itemCell.textContent?.trim() || 'Item';
    // Keyed by name and amount rather than position, so re-ordering the Notion
    // database does not silently transfer a tick from one item to another.
    const key = `${name}::${amount.cents}`;

    const label = document.createElement('label');
    label.className = 'notion-costs-pick';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'notion-costs-pick-input';
    checkbox.setAttribute('aria-label', `Include ${name} in my share`);
    const nameEl = document.createElement('span');
    nameEl.className = 'notion-costs-pick-name';
    nameEl.textContent = name;
    label.append(checkbox, nameEl);

    const countWrap = document.createElement('span');
    countWrap.className = 'notion-costs-pick-count';
    countWrap.hidden = true;
    const countInput = numberField('notion-costs-pick-count-input', 1);
    countInput.setAttribute('aria-label', `How many people share ${name}`);
    countWrap.append('shared by ', countInput, ' people');

    itemCell.textContent = '';
    itemCell.append(label, countWrap);

    const row_: PickableRow = {
      key, name, amountCents: amount.cents, checkbox, countInput, countWrap, overridden: false,
    };
    checkbox.addEventListener('change', onChange);
    countInput.addEventListener('input', () => {
      row_.overridden = true;
      onChange();
    });

    rows.push(row_);
  });

  return rows;
}

/**
 * The share calculator beneath the table.
 *
 * Two answers sit here, because they are different questions. The trip-wide
 * line divides the whole bill evenly and is what someone in on everything
 * wants. "Your share" only appears once items are ticked, and is for everyone
 * else — the person who skipped the rental car and half the dinners.
 */
function addSplitter(table: HTMLTableElement, slug: string, defaultParty: number | undefined): void {
  const total = readTotal(table);
  if (!total) return;
  const { cents: totalCents, symbol } = total;

  const namedInSettleUp = table
    .closest('.notion-costs')
    ?.querySelectorAll('.notion-costs-balances tbody tr').length ?? 0;

  const state = readState(slug);
  const initialParty = state.party ?? defaultParty ?? namedInSettleUp ?? 2;

  const box = document.createElement('div');
  box.className = 'notion-costs-split';

  const partyLabel = document.createElement('label');
  partyLabel.className = 'notion-costs-split-label';
  partyLabel.textContent = 'Split between';
  const partyInput = numberField('notion-costs-split-input', Math.max(1, initialParty));
  const unit = document.createElement('span');
  unit.className = 'notion-costs-split-unit';
  unit.textContent = 'people';
  partyLabel.append(partyInput, unit);

  const evenLine = document.createElement('p');
  evenLine.className = 'notion-costs-split-result';

  const hint = document.createElement('p');
  hint.className = 'notion-costs-split-hint muted small';
  hint.textContent = 'In on only some of it? Tick the items you shared, above.';

  const mine = document.createElement('div');
  mine.className = 'notion-costs-split-mine';
  mine.hidden = true;
  const mineLine = document.createElement('p');
  mineLine.className = 'notion-costs-split-result';
  const clear = document.createElement('button');
  clear.type = 'button';
  clear.className = 'secondary small notion-costs-split-clear';
  clear.textContent = 'Clear my picks';
  mine.append(mineLine, clear);

  const note = document.createElement('p');
  note.className = 'notion-costs-split-note muted small';

  const rows = buildRowPickers(table, () => render());

  // Restore previous picks before the first paint.
  for (const row of rows) {
    if (Object.prototype.hasOwnProperty.call(state.rows, row.key)) {
      row.checkbox.checked = true;
      const stored = state.rows[row.key];
      // A stored number means it was overridden; null means it was following.
      if (typeof stored === 'number' && Number.isInteger(stored)) {
        row.countInput.value = String(stored);
        row.overridden = true;
      }
    }
  }

  function render(): void {
    const party = clampCount(partyInput);

    const nextRows: Record<string, number | null> = {};
    let mineCents = 0;
    let picked = 0;

    for (const row of rows) {
      row.countWrap.hidden = !row.checkbox.checked;
      if (!row.checkbox.checked) continue;

      // A row the reader never touched follows the trip-wide number, so
      // changing that number moves every untouched row with it.
      if (!row.overridden) row.countInput.value = String(party);

      const shared = clampCount(row.countInput);
      // Rounded up per item so what everyone hands over always covers the bill.
      mineCents += Math.ceil(row.amountCents / shared);
      picked++;
      nextRows[row.key] = row.overridden ? shared : null;
    }

    const each = Math.ceil(totalCents / party);
    evenLine.textContent = '';
    const evenAmount = document.createElement('strong');
    evenAmount.textContent = formatMoney(each, symbol);
    evenLine.append(evenAmount, ` each, if the ${formatMoney(totalCents, symbol)} is shared evenly.`);

    mine.hidden = picked === 0;
    hint.hidden = picked > 0 || rows.length === 0;
    if (picked > 0) {
      mineLine.textContent = '';
      const mineAmount = document.createElement('strong');
      mineAmount.textContent = formatMoney(mineCents, symbol);
      mineLine.append(
        'Your share: ',
        mineAmount,
        ` across ${picked} item${picked === 1 ? '' : 's'}.`,
      );
    }

    // The settle-up above is computed only from people named in the rows. When
    // the party is bigger than that, its numbers are answering a different
    // question, and saying so beats quietly contradicting these lines.
    note.hidden = !(namedInSettleUp > 0 && party > namedInSettleUp);
    if (!note.hidden) {
      note.textContent =
        `“Who owes what” above only counts the ${namedInSettleUp} ` +
        `${namedInSettleUp === 1 ? 'person' : 'people'} named in the table, not all ${party}.`;
    }

    state.party = party;
    state.rows = nextRows;
    writeState(slug, state);
  }

  partyInput.addEventListener('input', render);
  clear.addEventListener('click', () => {
    for (const row of rows) row.checkbox.checked = false;
    render();
  });

  box.append(partyLabel, evenLine, hint, mine, note);
  render();

  table.closest('.notion-costs-scroll')?.after(box);
}

export function enhanceCostTables(container: HTMLElement, slug = '', partySize?: number): void {
  container.querySelectorAll<HTMLTableElement>('table.notion-costs-table').forEach((table) => {
    // A <table> that is its own scroll container has to be display:block, which
    // makes it shrink to its content — so a narrow table's Total row stops
    // short of the page. Giving it a wrapper to scroll in lets the table go
    // back to being a full-width table. The CSS keeps working unwrapped, so an
    // itinerary rendered before this shipped still scrolls rather than clips.
    if (!table.parentElement?.classList.contains('notion-costs-scroll')) {
      const scroller = document.createElement('div');
      scroller.className = 'notion-costs-scroll';
      table.replaceWith(scroller);
      scroller.appendChild(table);
    }

    const headers = Array.from(table.querySelectorAll<HTMLElement>('thead th')).map(
      (th) => th.textContent?.trim() ?? '',
    );
    if (headers.length === 0) return;

    table.querySelectorAll<HTMLTableRowElement>('tbody tr').forEach((row) => {
      Array.from(row.cells).forEach((cell, i) => {
        // A cell with no header above it (or an empty one) gets no label rather
        // than an empty "​: " prefix in the stacked layout.
        const label = headers[i];
        if (label) cell.setAttribute('data-label', label);
        // An empty cell is a column this row does not use. In a table it is
        // just a gap; as a stacked card it would be a labelled blank line.
        if (!cell.textContent?.trim()) cell.classList.add('is-empty');
      });
    });

    // Only the expense table has a Total row; the balances table shares the
    // class but has nothing to divide.
    if (!table.classList.contains('notion-costs-balances')) {
      addSplitter(table, slug, partySize);
    }
  });
}
